import { useMemo, useEffect, useState } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum, fmtDate } from "@/lib/format";
import { 
  CreditCard, TrendingUp, TrendingDown, Wallet, BarChart3, 
  ArrowUpRight, Zap, Calendar, ArrowRightLeft, Clock,
  CheckCircle2, AlertCircle, User, Download
} from "lucide-react";
import { cn } from "@/lib/utils";
import { exportFinanceiroToPDF } from "@/lib/export-pdf";

interface Duplicata {
  nDup: string;
  dVenc: string;
  vDup: number;
  entidade: string;
  tipo: "PAGAR" | "RECEBER";
  chave: string;
}

export default function FinanceiroPage() {
  const { filteredNotas, dashboard, privacyMode } = useFiscalStore();
  const [tab, setTab] = useState<"todos" | "pagar" | "receber">("todos");

  useEffect(() => { document.title = "Gestão Financeira — DataGrid XML"; }, []);

  const financeData = useMemo(() => {
    const topCnpj = dashboard?.cnpjEmpresa?.replace(/\D/g, "");
    const duplicatas: Duplicata[] = [];
    let totalPagar = 0;
    let totalReceber = 0;

    filteredNotas.forEach(n => {
      let op = n.tipoOperacao;
      if (op === "INDEFINIDA" && topCnpj) {
        if (n.cnpjDest.replace(/\D/g, "") === topCnpj) op = "ENTRADA";
        else if (n.cnpjEmit.replace(/\D/g, "") === topCnpj) op = "SAIDA";
      }

      const tipo: "PAGAR" | "RECEBER" = op === "ENTRADA" ? "PAGAR" : "RECEBER";
      const entidade = tipo === "PAGAR" ? n.emitente : n.destinatario;

      // Tenta extrair do bloco de cobrança (Duplicatas)
      const cobr = n.detalhes?.cobranca as any;
      if (cobr?.dup) {
        const dups = Array.isArray(cobr.dup) ? cobr.dup : [cobr.dup];
        dups.forEach((d: any) => {
          const val = parseFloat(d.vDup) || 0;
          duplicatas.push({
            nDup: d.nDup || n.numero,
            dVenc: d.dVenc || n.emissao.split("T")[0],
            vDup: val,
            entidade: entidade || "N/A",
            tipo,
            chave: n.chave
          });
          if (tipo === "PAGAR") totalPagar += val;
          else totalReceber += val;
        });
      } else {
        // Fallback: se não houver duplicatas, considera o valor total da nota como vencimento único na data de emissão
        const val = n.valorOficial;
        duplicatas.push({
          nDup: n.numero,
          dVenc: n.emissao.split("T")[0],
          vDup: val,
          entidade: entidade || "N/A",
          tipo,
          chave: n.chave
        });
        if (tipo === "PAGAR") totalPagar += val;
        else totalReceber += val;
      }
    });

    const sorted = duplicatas.sort((a, b) => a.dVenc.localeCompare(b.dVenc));
    
    return { 
      duplicatas: sorted, 
      totalPagar, 
      totalReceber, 
      count: duplicatas.length,
      hoje: new Date().toISOString().split("T")[0]
    };
  }, [filteredNotas, dashboard]);

  const filteredDups = useMemo(() => {
    if (tab === "todos") return financeData.duplicatas;
    return financeData.duplicatas.filter(d => d.tipo === (tab === "pagar" ? "PAGAR" : "RECEBER"));
  }, [financeData, tab]);

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-l-4 border-amber-500 pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="size-3 text-amber-500 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-amber-500">Financial Flow Control</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Financeiro <span className="text-amber-500">Contas</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
             <span className="flex items-center gap-2 uppercase tabular-nums"><Zap className="size-3.5 text-amber-500" /> Accounts Payable & Receivable</span>
             <span className="flex items-center gap-2 uppercase tabular-nums"><ArrowRightLeft className="size-3.5 text-emerald-500" /> Duplicatas extraídas do XML</span>
          </p>
        </div>

        <div className="flex gap-2 p-1 glass-technical rounded-2xl bg-white/5">
           {(["todos", "pagar", "receber"] as const).map(t => (
             <button 
               key={t}
               onClick={() => setTab(t)}
               className={cn(
                 "px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                 tab === t ? "bg-amber-500 text-black shadow-lg" : "hover:bg-white/5 text-muted-foreground"
               )}
             >
               {t}
             </button>
           ))}
        </div>
      </header>

      {/* KPIs Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        <FinanceCard 
          title="Contas a Receber" 
          value={financeData.totalReceber} 
          icon={TrendingUp} 
          color="text-primary" 
          subtitle="Faturamento de Clientes"
          percentage={100}
        />
        <FinanceCard 
          title="Contas a Pagar" 
          value={financeData.totalPagar} 
          icon={TrendingDown} 
          color="text-red-500" 
          subtitle="Compromissos com Fornecedores"
          percentage={100}
        />
        <FinanceCard 
          title="Fluxo Líquido" 
          value={financeData.totalReceber - financeData.totalPagar} 
          icon={Wallet} 
          color={(financeData.totalReceber - financeData.totalPagar) >= 0 ? "text-emerald-500" : "text-red-500"} 
          subtitle="Saldo Projetado (XML)"
          percentage={100}
        />
        <FinanceCard 
          title="Volume Financeiro" 
          value={financeData.count} 
          isCurrency={false}
          icon={BarChart3} 
          color="text-amber-500" 
          subtitle="Total de Duplicatas"
          percentage={100}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Main List */}
        <div className="xl:col-span-2 glass-card border-primary/5 bg-muted/5 overflow-hidden">
           <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
              <div className="flex items-center gap-3">
                 <div className="size-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                    <Clock className="size-5" />
                 </div>
                 <div>
                    <h2 className="text-lg font-black tracking-tight uppercase">Cronograma de Vencimentos</h2>
                    <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">Compromissos extraídos diretamente da cobrança XML</p>
                 </div>
              </div>
           </div>

           <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-white/5 text-[9px] font-black uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="p-5">Vencimento</th>
                    <th className="p-5">Entidade</th>
                    <th className="p-5">Tipo</th>
                    <th className="p-5">Documento</th>
                    <th className="p-5 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                   {filteredDups.slice(0, 100).map((d, i) => {
                     const isVencida = d.dVenc < financeData.hoje;
                     const isHoje = d.dVenc === financeData.hoje;
                     
                     return (
                       <tr key={i} className="group hover:bg-white/[0.02] transition-colors">
                         <td className="p-5">
                            <div className="flex items-center gap-2">
                               <div className={cn(
                                 "size-2 rounded-full",
                                 isVencida ? "bg-red-500 animate-pulse" : isHoje ? "bg-amber-500" : "bg-emerald-500"
                               )} />
                               <span className="text-xs font-black tabular-nums">{fmtDate(d.dVenc)}</span>
                            </div>
                         </td>
                         <td className="p-5">
                            <div className="flex items-center gap-2">
                               <User className="size-3 text-muted-foreground" />
                               <span className="text-xs font-bold truncate max-w-[200px] uppercase tracking-tight">
                                  {d.entidade}
                               </span>
                            </div>
                         </td>
                         <td className="p-5">
                            <span className={cn(
                              "text-[8px] font-black px-2 py-0.5 rounded-md border uppercase tracking-tighter",
                              d.tipo === "RECEBER" ? "bg-primary/10 text-primary border-primary/20" : "bg-red-500/10 text-red-500 border-red-500/20"
                            )}>
                               {d.tipo}
                            </span>
                         </td>
                         <td className="p-5 text-[10px] font-black text-muted-foreground tabular-nums">
                            #{d.nDup}
                         </td>
                         <td className="p-5 text-right font-black text-sm tabular-nums tracking-tighter">
                            {fmtBRL(d.vDup)}
                         </td>
                       </tr>
                     );
                   })}
                   {filteredDups.length === 0 && (
                     <tr>
                        <td colSpan={5} className="p-32 text-center text-muted-foreground">
                           <div className="flex flex-col items-center gap-3 opacity-20">
                              <CheckCircle2 className="size-12" />
                              <span className="text-[10px] font-black uppercase tracking-widest">Nenhum compromisso financeiro pendente</span>
                           </div>
                        </td>
                     </tr>
                   )}
                </tbody>
              </table>
           </div>
        </div>

        {/* Sidebar Insights */}
        <div className="space-y-6">
           <div className="glass-card p-8 border-primary/5 bg-primary/5 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
                <TrendingUp className="size-24" />
              </div>
              <h3 className="text-sm font-black uppercase tracking-widest mb-6 flex items-center gap-2">
                 <AlertCircle className="size-4 text-primary" /> Alertas de Caixa
              </h3>
              <div className="space-y-4">
                 <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                    <span className="text-[8px] font-black text-muted-foreground uppercase block mb-1">Vencendo Hoje</span>
                    <span className="text-xl font-black text-amber-500">
                       {fmtBRL(financeData.duplicatas.filter(d => d.dVenc === financeData.hoje).reduce((acc, d) => acc + d.vDup, 0))}
                    </span>
                 </div>
                 <div className="p-4 rounded-2xl bg-white/5 border border-white/5">
                    <span className="text-[8px] font-black text-muted-foreground uppercase block mb-1">Vencidos (Inadimplência Est.)</span>
                    <span className="text-xl font-black text-red-500">
                       {fmtBRL(financeData.duplicatas.filter(d => d.dVenc < financeData.hoje).reduce((acc, d) => acc + d.vDup, 0))}
                    </span>
                 </div>
              </div>
           </div>

           <div className="glass-card p-8 border-emerald-500/5 bg-emerald-500/5">
              <h3 className="text-sm font-black uppercase tracking-widest mb-4">Exportação Financeira</h3>
              <p className="text-[10px] font-bold text-muted-foreground/60 uppercase leading-relaxed mb-8">
                 Envie o cronograma de contas a pagar e receber para seu setor financeiro ou contábil.
              </p>
              <button 
                onClick={() => exportFinanceiroToPDF(
                  filteredDups,
                  {
                    title: `Relatório Financeiro — ${tab.toUpperCase()}`,
                    filename: "financeiro_compromissos",
                    companyName: dashboard?.nomeEmpresa,
                    cnpj: dashboard?.cnpjEmpresa,
                    periodo: useFiscalStore.getState().filtroPeriodo
                  }
                )}
                className="w-full h-14 rounded-2xl bg-emerald-500 text-black font-black uppercase tracking-widest text-[10px] shadow-xl shadow-emerald-500/20 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
              >
                 <Download className="size-4" /> Exportar Relatório PDF
              </button>
           </div>
        </div>
      </div>
    </div>
  );
}

function FinanceCard({ title, value, icon: Icon, color, subtitle, percentage, isCurrency = true }: any) {
  return (
    <div className="glass-card p-6 border-primary/5 bg-muted/5 hover:border-primary/20 transition-all group">
      <div className="flex justify-between items-start mb-4">
        <div className={cn("size-12 rounded-2xl flex items-center justify-center bg-white/5 border border-foreground/5 shadow-lg group-hover:rotate-6 transition-all", color)}>
          <Icon className="size-6" />
        </div>
        <div className="flex flex-col items-end">
           <div className={cn("flex items-center gap-1 text-[10px] font-black tabular-nums", color)}>
             {percentage}% <ArrowUpRight className="size-3" />
           </div>
           <span className="text-[8px] font-black text-muted-foreground uppercase tracking-widest mt-1">XML Data Integrity</span>
        </div>
      </div>
      
      <div className="space-y-1">
        <h3 className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">{title}</h3>
        <div className="text-2xl font-black tracking-tighter tabular-nums">
          {isCurrency ? fmtBRL(value) : fmtNum(value)}
        </div>
        <p className="text-[10px] font-bold text-muted-foreground/60 uppercase tracking-tight">{subtitle}</p>
      </div>
    </div>
  );
}
